import { useEffect, useState } from "react";
import type { SlotProps } from "../../../core/platform/registry/registry";
import type { SaleFinalizedEvent } from "../../../core/domain/events/bus";
import { useAuth } from "../../../core/platform/auth/AuthProvider";
import { getMeta, META_KEYS } from "../../../core/data/local/db";
import { earnFor } from "../data/records";
import { recordEarn } from "../subscriber";
import { ScanCard, type AcceptedCard } from "./ScanCard";

/**
 * "Add card" on a sale already rung (specs/006-loyalty US5, FR-031).
 *
 * For the customer who comes back a minute later with their card. Offered only
 * while the sale's shift is still open, only for a sale that is not voided,
 * and only once: a sale with a card already has its stickers. The server
 * enforces "once" as well, whichever till asks (research R14). Rewards cannot
 * be redeemed this way — a reward needs the card at the counter, in the cart.
 */

interface AddCardProps {
  saleId: string;
  shiftId: string;
  shiftOpen: boolean;
  status: string;
  loadLines(): Promise<SaleFinalizedEvent["lines"]>;
}

export function AddCard(props: SlotProps) {
  const { saleId, shiftId, shiftOpen, status, loadLines } = props as unknown as AddCardProps;
  const { claims } = useAuth();
  const [state, setState] = useState<"checking" | "available" | "has-card" | "scanning" | "saving" | "done" | "none">(
    "checking",
  );
  const [added, setAdded] = useState(0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!shiftOpen || status === "voided") {
      setState("none");
      return;
    }
    void earnFor(saleId).then((earn) => {
      if (!cancelled) setState(earn ? "has-card" : "available");
    });
    return () => {
      cancelled = true;
    };
  }, [saleId, shiftOpen, status]);

  const accept = async (card: AcceptedCard) => {
    setState("saving");
    setError(null);
    try {
      if (await earnFor(saleId)) {
        setState("has-card");
        return;
      }
      const record = await recordEarn({
        source: "attach",
        saleId,
        shiftId,
        staffId: claims?.sub ?? "",
        terminalId: (await getMeta<string>(META_KEYS.terminalId)) ?? "",
        cardCode: card.code,
        digest: card.digest,
        lines: await loadLines(),
        redemptions: [],
        createdAt: new Date().toISOString(),
      });
      setAdded(record?.stickers ?? 0);
      setState("done");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not add the card");
      setState("available");
    }
  };

  if (state === "none" || state === "checking") return null;
  if (state === "has-card") return <span className="loy__tag">Card added</span>;
  if (state === "done") {
    return <span className="loy__tag">{added > 0 ? `Card added · +${added}` : "Card added · no drinks to count"}</span>;
  }

  return (
    <>
      <button type="button" className="loy__add" onClick={() => setState("scanning")} disabled={state === "saving"}>
        Add card
      </button>
      {error && <span className="loy__error">{error}</span>}
      {state === "scanning" && <ScanCard onAccept={(card) => void accept(card)} onClose={() => setState("available")} />}
    </>
  );
}
